// src/tenantAdmin/userActionError.js
//
// Our words for a failed user-management call on Gestione Workspace. ⛔ Never the
// server's own sentence: buildApiError always fills it with the API's English
// prose. Branch on `err.code` only.
//
// ⚠️ Consult membershipRefusalCopy (./membershipRefusal.js) FIRST: the two
// "belongs elsewhere" 409s need a per-action remedy this helper cannot know.
//
// Codes the active backend (`dev`) can return on these routes — list, role,
// profile, password, deactivate, remove, and the state-gated three (resend an
// invitation, reactivate, correct the address) plus the workspace rename — are
// mapped below; anything else (a 5xx, a network failure, an unexpected 422) gets
// the generic sentence with the code, a handle for support.
//
// ⚠️ The same code can arrive from more than one route and still means one thing
// to the reader, which is why this is a flat table and not a per-action one:
// USER_SEAT_LIMIT_REACHED answers an invite AND a reactivation, and both times
// the remedy is the company's seats.
//
// ⛔ A separate table from the superadmin AssignTenantModal's: a different route
// with its own vocabulary (there "no such account" is RESOURCE_NOT_FOUND, here
// USER_NOT_FOUND). ⛔ CANNOT_DEMOTE_SELF is deliberately absent: it is a company
// route refusal, worded by CompanyAdminsSection with its own guard sentence.
const USER_GONE = new Set(['USER_TENANT_ASSIGNMENT_NOT_FOUND', 'USER_NOT_FOUND']);

const KEYS = {
  CANNOT_RESET_OWN_PASSWORD: 'tenantUsers.cannotResetOwnPassword',
  CANNOT_EDIT_USER: 'tenantUsers.cannotEditUser',
  INVALID_ROLE: 'tenantUsers.invalidRole',
  // Never activated: there is no password to restore, so reactivating is the
  // wrong control — the person needs a fresh invitation link.
  USER_NEVER_ONBOARDED: 'tenantUsers.neverOnboarded',
  // Nothing to resend: the account is already active, or it was deactivated.
  USER_NOT_PENDING_INVITE: 'tenantUsers.notPendingInvite',
  // The company would be left with nobody able to administer it.
  CANNOT_REMOVE_LAST_CUSTOMER_ADMIN: 'tenantUsers.lastCustomerAdmin',
  CANNOT_DEACTIVATE_SELF: 'tenantUsers.cannotDeactivateSelf',
  // The one key outside `tenantUsers`; the same `admin`-bound `t` resolves it.
  CANNOT_EDIT_OWN_EMAIL: 'manage.cannotEditOwnEmail',
  // A correction (or an invite) aimed at an address another account already holds.
  AUTH_EMAIL_EXISTS: 'tenantUsers.emailTaken',
  // The rename: 1–100 characters.
  INVALID_DISPLAY_NAME: 'tenantUsers.invalidDisplayName',
  // People, not workspaces — the company-wide person cap.
  USER_SEAT_LIMIT_REACHED: 'seats.limitReached',
};

/**
 * Our copy for a refused user-management write (or a failed list read).
 *
 * @param {(key: string, vars?: object) => string} t i18n `t` bound to `admin`
 * @param {Error & {code?: string, status?: number}} err the rejection
 * @returns {string} a sentence we authored — never the server's
 */
export function userActionErrorCopy(t, err) {
  const code = err?.code;
  if (USER_GONE.has(code)) return t('tenantUsers.userGone');
  // 429: the wait is the whole message, so the generic wording is used only when
  // the envelope carried no number (the header-less case). `Number.isFinite`,
  // not truthiness: a wait of 0 is still a number to show.
  if (code === 'RATE_LIMITED') {
    const seconds = err?.retry_after_seconds;
    return Number.isFinite(seconds)
      ? t('tenantUsers.rateLimited', { seconds })
      : t('tenantUsers.rateLimitedGeneric');
  }
  if (KEYS[code]) return t(KEYS[code]);
  // ⭐ Since BE dbd8a73 an unexpected server error is a CORS-safe JSON 500
  // (`INTERNAL_ERROR`) carrying `request_id`: THAT is the handle support can
  // trace in the logs, so it replaces the bare code. ⛔ Never the server's message.
  if (err?.status >= 500 && err?.requestId) return t('tenantUsers.serverError', { requestId: err.requestId });
  return t('tenantUsers.genericError', { code: code || err?.status || '—' });
}

export default userActionErrorCopy;
